/**
 * ItemSelect - 商品选择器公共组件
 *
 * 功能：
 * - 支持 item code 和 item name 同时模糊搜索（OR 关系，后端 /api/item/option 统一处理）
 * - 下拉项展示 "itemCode - itemName"
 * - 组件值(value)为 itemCode，传给后端的也是 itemCode
 * - 内置 300ms 防抖，避免频繁请求
 * - 支持受控和非受控模式
 *
 * 使用示例：
 * <Form.Item name="code" label="Item Code/Name">
 *   <ItemSelect placeholder="Search item code or name" />
 * </Form.Item>
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Select, Spin } from 'antd';
import type { SelectProps } from 'antd';
import * as itemService from '../../services/master-data/itemService';

export interface ItemSelectProps extends Omit<SelectProps<string>, 'options' | 'onSearch' | 'filterOption'> {
  /** 防抖延时(ms)，默认 300 */
  debounceMs?: number;
}

const ItemSelect: React.FC<ItemSelectProps> = ({
  debounceMs = 300,
  placeholder = 'Search item code / name',
  ...restProps
}) => {
  const [options, setOptions] = useState<{ label: string; value: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 请求商品下拉选项（后端按 code OR name 模糊匹配，label 已格式化为 "code - name"）
  const fetchItems = useCallback(async (keyword: string) => {
    setLoading(true);
    try {
      const resp = await itemService.itemOption({ code: keyword });
      if (resp.success && resp.data) {
        setOptions(resp.data.map((opt) => ({ label: opt.label, value: opt.value })));
      } else {
        setOptions([]);
      }
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // 搜索处理（带防抖，输入 2 个字符以上才发起请求）
  const handleSearch = useCallback(
    (value: string) => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      if (!value || value.length < 2) {
        setOptions([]);
        return;
      }
      debounceTimer.current = setTimeout(() => {
        fetchItems(value);
      }, debounceMs);
    },
    [fetchItems, debounceMs]
  );

  // 清理 timer
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  return (
    <Select
      showSearch
      allowClear
      placeholder={placeholder}
      filterOption={false}
      onSearch={handleSearch}
      notFoundContent={loading ? <Spin size="small" /> : null}
      options={options}
      {...restProps}
    />
  );
};

export default ItemSelect;
